import { useEffect, useState } from "react";
import { api, formatUGX } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CheckCircle2, XCircle, Trash2, Inbox, Building2, Clock, BarChart3, Star, Search, Wallet, Save } from "lucide-react";
import { toast } from "sonner";

const ADMIN_PASSCODE = "tigawa";

export default function Admin() {
  const [authed, setAuthed] = useState(typeof window !== "undefined" && sessionStorage.getItem("tpc_admin") === "1");
  const [pin, setPin] = useState("");

  if (!authed) {
    return (
      <main className="max-w-md mx-auto px-6 py-32" data-testid="admin-gate">
        <div className="eyebrow mb-3">Admin</div>
        <h1 className="font-display text-3xl tracking-tighter">Restricted area</h1>
        <p className="text-muted-foreground mt-2 text-sm">Enter passcode to continue.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (pin === ADMIN_PASSCODE) { sessionStorage.setItem("tpc_admin", "1"); setAuthed(true); }
            else toast.error("Wrong passcode");
          }}
          className="mt-6 flex gap-3"
        >
          <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Passcode" data-testid="admin-pin" />
          <Button type="submit" className="rounded-full bg-primary hover:bg-primary/90" data-testid="admin-pin-submit">Enter</Button>
        </form>
        <p className="text-xs text-muted-foreground mt-4 font-mono">Demo passcode: tigawa</p>
      </main>
    );
  }

  return <AdminDashboard />;
}

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");

  const fetchAll = async () => {
    const [s, p, pa, i] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/properties", { params: { status: "pending", limit: 200 } }),
      api.get("/properties", { params: { status: "approved", limit: 200 } }),
      api.get("/inquiries"),
    ]);
    setStats(s.data);
    setProperties([...p.data.map((x) => ({ ...x, _bucket: "pending" })), ...pa.data.map((x) => ({ ...x, _bucket: "approved" }))]);
    setInquiries(i.data);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [s, p, pa, i] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/properties", { params: { status: "pending", limit: 200 } }),
        api.get("/properties", { params: { status: "approved", limit: 200 } }),
        api.get("/inquiries"),
      ]);
      if (cancelled) return;
      setStats(s.data);
      setProperties([...p.data.map((x) => ({ ...x, _bucket: "pending" })), ...pa.data.map((x) => ({ ...x, _bucket: "approved" }))]);
      setInquiries(i.data);
    })();
    return () => { cancelled = true; };
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/properties/${id}`, { status });
      toast.success(`Listing ${status}`);
      fetchAll();
    } catch { toast.error("Failed"); }
  };
  const toggleFeatured = async (id, featured) => {
    try { await api.patch(`/properties/${id}`, { featured: !featured }); toast.success(featured ? "Unfeatured" : "Featured"); fetchAll(); } catch { toast.error("Failed"); }
  };
  const deleteProp = async (id) => {
    try { await api.delete(`/properties/${id}`); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); }
  };
  const deleteInq = async (id) => {
    try { await api.delete(`/inquiries/${id}`); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); }
  };

  const list = properties.filter((p) => p._bucket === tab).filter((p) => !q || p.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <main className="min-h-screen bg-muted/30" data-testid="admin-dashboard">
      <div className="bg-secondary text-secondary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-accent">Control Room</div>
            <h1 className="font-display text-2xl md:text-3xl tracking-tight mt-1">TIGAWA PROPERTIES — Admin</h1>
          </div>
          <Button
            variant="outline"
            className="bg-transparent border-white/30 text-secondary-foreground hover:bg-white/10 rounded-full"
            onClick={() => { sessionStorage.removeItem("tpc_admin"); window.location.reload(); }}
            data-testid="admin-logout"
          >
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <Stat icon={Building2} label="Total Properties" value={stats?.total_properties ?? "—"} />
          <Stat icon={Clock} label="Pending" value={stats?.pending ?? "—"} accent />
          <Stat icon={CheckCircle2} label="Approved" value={stats?.approved ?? "—"} />
          <Stat icon={XCircle} label="Rejected" value={stats?.rejected ?? "—"} />
          <Stat icon={Inbox} label="Inquiries" value={stats?.inquiries ?? "—"} />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mb-6" data-testid="admin-tabs">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList className="bg-card border border-border rounded-full p-1">
              <TabsTrigger value="pending" className="rounded-full" data-testid="tab-pending">Pending ({stats?.pending ?? 0})</TabsTrigger>
              <TabsTrigger value="approved" className="rounded-full" data-testid="tab-approved">Approved ({stats?.approved ?? 0})</TabsTrigger>
              <TabsTrigger value="inquiries" className="rounded-full" data-testid="tab-inquiries">Inquiries ({stats?.inquiries ?? 0})</TabsTrigger>
              <TabsTrigger value="rates" className="rounded-full" data-testid="tab-rates">Bank Rates</TabsTrigger>
            </TabsList>
            {tab !== "inquiries" && (
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input className="pl-9 rounded-full" placeholder="Search title…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search" />
              </div>
            )}
          </div>

          <TabsContent value="pending" className="mt-6">
            <PropertyTable list={list} onApprove={(id) => updateStatus(id, "approved")} onReject={(id) => updateStatus(id, "rejected")} onDelete={deleteProp} onFeature={toggleFeatured} />
          </TabsContent>
          <TabsContent value="approved" className="mt-6">
            <PropertyTable list={list} onApprove={(id) => updateStatus(id, "approved")} onReject={(id) => updateStatus(id, "rejected")} onDelete={deleteProp} onFeature={toggleFeatured} />
          </TabsContent>
          <TabsContent value="inquiries" className="mt-6">
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <Table>
                <TableHeader className="bg-muted/40">
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Interest</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inquiries.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No inquiries yet</TableCell></TableRow>
                  ) : inquiries.map((i) => (
                    <TableRow key={i.id} data-testid={`inq-row-${i.id}`}>
                      <TableCell className="font-medium">{i.name}</TableCell>
                      <TableCell className="font-mono text-xs">{i.phone}</TableCell>
                      <TableCell><span className="px-2 py-0.5 rounded-full bg-accent/20 text-xs">{i.interest}</span></TableCell>
                      <TableCell className="max-w-xs truncate text-xs text-muted-foreground">{i.message || "—"}</TableCell>
                      <TableCell className="text-right">
                        <ConfirmDelete onConfirm={() => deleteInq(i.id)} tid={`del-inq-${i.id}`} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          <TabsContent value="rates" className="mt-6">
            <BankRatesEditor />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl p-5 border ${accent ? "bg-primary/5 border-primary/30" : "bg-card border-border"}`}>
      <Icon className={`w-4 h-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
      <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground mt-3">{label}</div>
      <div className="font-display text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}

function PropertyTable({ list, onApprove, onReject, onDelete, onFeature }) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <Table>
        <TableHeader className="bg-muted/40">
          <TableRow>
            <TableHead>Property</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Pkg</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.length === 0 ? (
            <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nothing here yet</TableCell></TableRow>
          ) : list.map((p) => (
            <TableRow key={p.id} data-testid={`prop-row-${p.id}`}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <img src={p.images?.[0]} alt="" className="w-12 h-12 rounded-lg object-cover border border-border" />
                  <div>
                    <div className="font-medium text-sm line-clamp-1">{p.title}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{p.property_type} · {p.title_status}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-xs">{p.district}, {p.region}</TableCell>
              <TableCell className="text-xs"><div>{p.owner_name}</div><div className="font-mono text-muted-foreground">{p.owner_phone}</div></TableCell>
              <TableCell className="text-sm font-semibold text-primary">{formatUGX(p.price_ugx)}</TableCell>
              <TableCell><span className="px-2 py-0.5 rounded-full bg-secondary/10 text-secondary text-xs">{p.package}</span></TableCell>
              <TableCell className="text-right">
                <div className="inline-flex items-center gap-1">
                  {p._bucket !== "approved" && (
                    <Button size="sm" onClick={() => onApprove(p.id)} className="rounded-full h-8 px-3 bg-primary hover:bg-primary/90" data-testid={`approve-${p.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                    </Button>
                  )}
                  {p._bucket === "approved" && (
                    <Button size="sm" variant="outline" onClick={() => onFeature(p.id, p.featured)} className="rounded-full h-8 px-3 border-border" data-testid={`feature-${p.id}`}>
                      <Star className={`w-3.5 h-3.5 ${p.featured ? "text-accent fill-accent" : ""}`} /> {p.featured ? "Unfeature" : "Feature"}
                    </Button>
                  )}
                  {p._bucket !== "approved" && (
                    <Button size="sm" variant="outline" onClick={() => onReject(p.id)} className="rounded-full h-8 px-3 border-border" data-testid={`reject-${p.id}`}>
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </Button>
                  )}
                  <ConfirmDelete onConfirm={() => onDelete(p.id)} tid={`del-prop-${p.id}`} />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ConfirmDelete({ onConfirm, tid }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full h-8 px-3 border-destructive/40 text-destructive hover:bg-destructive/10" data-testid={tid}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this?</AlertDialogTitle>
          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-destructive hover:bg-destructive/90" data-testid={`${tid}-confirm`}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function BankRatesEditor() {
  const [rates, setRates] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/bank-rates").then((r) => { setRates(r.data.rates || []); setUpdatedAt(r.data.updated_at); });
  }, []);

  const update = (i, key, val) => {
    const copy = [...rates];
    copy[i] = { ...copy[i], [key]: key === "bank" ? val : Number(val) };
    setRates(copy);
  };
  const remove = (i) => setRates(rates.filter((_, idx) => idx !== i));
  const add = () => setRates([...rates, { bank: "", rate_apr: 18, term_max_years: 15, min_deposit_pct: 20 }]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/bank-rates", { rates });
      const r = await api.get("/bank-rates");
      setUpdatedAt(r.data.updated_at);
      toast.success("Bank rates saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6" data-testid="bank-rates-editor">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-4 h-4 text-primary" />
            <div className="eyebrow">Live Mortgage Rates</div>
          </div>
          <p className="text-sm text-muted-foreground">Edit each partner bank&apos;s current APR — used across property calculators.</p>
          {updatedAt && <p className="text-[11px] text-muted-foreground mt-1 font-mono">Last updated {new Date(updatedAt).toLocaleString()}</p>}
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="rates-save-btn">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save rates"}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-muted/40">
            <TableRow>
              <TableHead>Bank</TableHead>
              <TableHead>APR %</TableHead>
              <TableHead>Max term (years)</TableHead>
              <TableHead>Min deposit %</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rates.map((r, i) => (
              <TableRow key={i}>
                <TableCell><Input value={r.bank} onChange={(e) => update(i, "bank", e.target.value)} data-testid={`rate-bank-${i}`} /></TableCell>
                <TableCell><Input type="number" step="0.1" value={r.rate_apr} onChange={(e) => update(i, "rate_apr", e.target.value)} data-testid={`rate-apr-${i}`} className="w-24" /></TableCell>
                <TableCell><Input type="number" value={r.term_max_years} onChange={(e) => update(i, "term_max_years", e.target.value)} className="w-20" /></TableCell>
                <TableCell><Input type="number" value={r.min_deposit_pct} onChange={(e) => update(i, "min_deposit_pct", e.target.value)} className="w-20" /></TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => remove(i)} className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10 h-8 w-8 p-0"><Trash2 className="w-3.5 h-3.5" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Button onClick={add} variant="outline" className="mt-4 rounded-full border-border" data-testid="rates-add-btn">
        + Add another bank
      </Button>
    </div>
  );
}
